// Opt-in precise geolocation for the chat.
//
// IP geolocation (the default) is inherently coarse — city-level at best and
// often wrong for mobile/CGNAT/VPN clients. When the user opts in, we read the
// browser Geolocation API (GPS/Wi-Fi, ~10–50 m) and send the raw coordinates to
// the backend, which reverse-geocodes them to bairro/cidade (Google Geocoding,
// Redis-cached) and prefers them over the IP fallback. Reverse-geocoding lives
// server-side so the provider/key are centralized and the Redis cache absorbs
// load (users in the same ~110 m cell share one lookup).

export interface PreciseLocation {
  lat: number;
  lng: number;
  // Radius of the browser's 95% confidence circle, in metres. Sent to the
  // backend, which resolves the bairro only for fixes precise enough to
  // identify one.
  accuracy?: number;
}

// Stored (localStorage) shape: coords + capture time. capturedAt never leaves
// the client — request bodies send only {lat, lng, accuracy}.
export interface StoredPreciseLocation extends PreciseLocation {
  capturedAt: number;
}

// A fix older than this is not attached to requests: the backend then falls
// back to IP geolocation (right city) instead of replaying a stale bairro.
// iOS Safari reports the geolocation permission as 'prompt' even after the
// user allowed it (ask-per-session model), so the on-load silent refresh never
// runs there and, without this cutoff, a fix captured once at toggle-enable
// time would be replayed for days.
export const LOCATION_MAX_AGE_MS = 30 * 60 * 1000;

// Below this radius a fix pins the bairro; above it the backend keeps only the
// city and up. Phones (GPS) and laptops (Wi-Fi triangulation) land well under
// it; a desktop on Ethernet has no Wi-Fi to triangulate with, so the browser
// resolves the position from the IP and reports kilometres.
export const LOCATION_NEIGHBORHOOD_ACCURACY_M = 500;

// Past this radius the fix says no more than the IP fallback already does and
// can name the wrong city — discard it and let the backend geolocate the IP.
export const LOCATION_MAX_ACCURACY_M = 50 * 1000;

// Why a location request produced no fix. 'denied' means the browser refused:
// either the site permission is blocked or the OS-level location service is
// off (Windows/macOS surface that as a denial even when the site is allowed).
export type LocationFailureReason = 'denied' | 'unavailable' | 'timeout' | 'unsupported';

export type LocationResult =
  | { ok: true; location: StoredPreciseLocation }
  | { ok: false; reason: LocationFailureReason };

// Whether a stored fix is still worth sending. Entries persisted by older
// bundles have no capturedAt and count as stale (refreshed on next send).
export function isFreshLocation(
  loc: StoredPreciseLocation | null | undefined,
  nowMs: number,
): loc is StoredPreciseLocation {
  if (!loc || typeof loc.lat !== 'number' || typeof loc.lng !== 'number') return false;
  if (typeof loc.capturedAt !== 'number' || !Number.isFinite(loc.capturedAt) || loc.capturedAt <= 0) return false;
  return nowMs - loc.capturedAt < LOCATION_MAX_AGE_MS;
}

// Parse the localStorage payload defensively (user-editable storage). Coords
// missing/NaN → null; missing capturedAt (older bundles) → 0, i.e. stale;
// missing accuracy (older bundles) → undefined, which the backend reads as
// bairro-grade, the behaviour those bundles already had.
export function parseStoredLocation(raw: string | null): StoredPreciseLocation | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    const lat = Number(parsed?.lat);
    const lng = Number(parsed?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    const capturedAt = Number(parsed?.capturedAt);
    const accuracy = Number(parsed?.accuracy);
    const loc: StoredPreciseLocation = { lat, lng, capturedAt: Number.isFinite(capturedAt) ? capturedAt : 0 };
    if (Number.isFinite(accuracy)) loc.accuracy = accuracy;
    return loc;
  } catch {
    return null;
  }
}

// Current geolocation permission state, without triggering a prompt. Used to
// silently refresh the coords on app load when the user already granted it.
export async function geolocationPermissionState(): Promise<PermissionState | 'unsupported'> {
  try {
    if (typeof navigator === 'undefined' || !(navigator as any).permissions) return 'unsupported';
    const status = await (navigator as any).permissions.query({ name: 'geolocation' as PermissionName });
    return status.state as PermissionState;
  } catch {
    return 'unsupported';
  }
}

// Request the browser's coordinates (prompts for permission the first time). A
// coarse fix still comes back as a success — callers pass the accuracy along
// and the backend decides how specific a place name it supports. Failures
// carry a reason so the caller can tell "blocked", which the user can act on,
// apart from "no fix right now", which is retried on the next message.
export function requestPreciseLocation(opts?: { highAccuracy?: boolean }): Promise<LocationResult> {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve({ ok: false, reason: 'unsupported' });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const accuracy = pos.coords.accuracy;
        const hasAccuracy = typeof accuracy === 'number' && Number.isFinite(accuracy);
        if (hasAccuracy && accuracy > LOCATION_MAX_ACCURACY_M) {
          resolve({ ok: false, reason: 'unavailable' });
          return;
        }
        const location: StoredPreciseLocation = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          capturedAt: Date.now(),
        };
        if (hasAccuracy) location.accuracy = accuracy;
        resolve({ ok: true, location });
      },
      (err) => {
        const code = err?.code;
        if (code === 1) resolve({ ok: false, reason: 'denied' });
        else if (code === 3) resolve({ ok: false, reason: 'timeout' });
        else resolve({ ok: false, reason: 'unavailable' });
      },
      { enableHighAccuracy: opts?.highAccuracy ?? true, timeout: 10000, maximumAge: 300000 },
    );
  });
}
